// HO-only business org filter.
//
// An HO user can pick a business from the header dropdown. The chosen business
// *name* is persisted in sessionStorage (so it survives a reload) and injected
// into the body of the specific search/stats endpoints that support org
// filtering. See FE_HO_BUSINESS_CATEGORY_FILTER for the contract:
//   - Search APIs (B): fields go inside `searchFilter`.
//   - Stats APIs (C): fields go at the top level of the body.
//   - Values are string names, in arrays. `businesses` takes precedence over
//     `categories` (we only send `businesses` here).
//   - Non-HO users never set a selection, so nothing is injected for them.

const STORAGE_KEY = "ho_business_filter";
const STORAGE_KEY_CATEGORY = "ho_category_filter";

// Search-type endpoints: anything that POSTs to a `/search` path. The fields
// belong inside `searchFilter`. Matching by convention (rather than a fixed
// list) means new search endpoints are covered automatically.
const isSearchEndpoint = (url) => typeof url === "string" && /\/search(\?|$)/.test(url);

// Stats / analytics endpoints: the fields belong at the TOP LEVEL of the body.
// These don't follow a single naming convention, so they're listed explicitly.
// Extend this list as more analytics endpoints need HO business scoping.
const TOP_LEVEL_ENDPOINTS = [
  // daily-tracker compliance + task-item stats
  "daily-tracker/v1/stats/getTaskStatsTypeWise",
  "daily-tracker/v1/stats/getSELStats",
  "daily-tracker/v1/task-items/getTaskStatsTypeWise",
  "daily-tracker/v1/task-items/getTaskStatsForSEL",
  // lead dashboard analytics
  "quickapp/v1/lead/dashboard/daily-lead-volume",
  "quickapp/v1/lead/dashboard/lead-reason-count",
  "quickapp/v1/lead/dashboard/lead-by-category",
  "quickapp/v1/lead/dashboard/brand-wise-lead-count",
  // digital dashboard / PMI analytics (flat top-level { queryName, ...filters })
  // "pmi/dashboard_data" also matches "pmi/dashboard_data_v2" via substring.
  "pmi/dashboard_data",
  "pmi/dashboard_data_v2",
  "pmi/hd_articles_inventory",
  "so_search_sales_order",
  // home dashboard data
  "/home/dashboard",
];

const toArray = (value) =>
  Array.isArray(value) ? value : value === undefined || value === null || value === "" ? [] : [value];

export const setSelectedBusinesses = (businesses) => {
  const list = toArray(businesses).filter(Boolean);
  try {
    if (list.length) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch (e) {
    /* ignore storage errors */
  }
};

export const getSelectedBusinesses = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? toArray(JSON.parse(raw)).filter(Boolean) : [];
  } catch (e) {
    return [];
  }
};

// Category is the org dimension paired with business (from /fetch-access). It
// is persisted and injected the SAME way as businesses, alongside it.
export const setSelectedCategories = (categories) => {
  const list = toArray(categories).filter(Boolean);
  try {
    if (list.length) {
      sessionStorage.setItem(STORAGE_KEY_CATEGORY, JSON.stringify(list));
    } else {
      sessionStorage.removeItem(STORAGE_KEY_CATEGORY);
    }
  } catch (e) {
    /* ignore storage errors */
  }
};

export const getSelectedCategories = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY_CATEGORY);
    return raw ? toArray(JSON.parse(raw)).filter(Boolean) : [];
  } catch (e) {
    return [];
  }
};

// Seed the HO filter from the /fetch-access response ([{ value, name }] lists).
// Keeps a selection the user already made (e.g. survived a reload); otherwise
// defaults to the first business. Categories are always refreshed from access.
// Called as soon as fetch-access resolves so the very next request carries the
// filter — not only after a reload.
export const initHoFiltersFromAccess = ({ businesses, categories } = {}) => {
  const existing = getSelectedBusinesses();
  const chosen = existing.length
    ? existing
    : [businesses?.[0]?.value].filter(Boolean);
  if (!chosen.length) return chosen;
  setSelectedBusinesses(chosen);
  setSelectedCategories((categories || []).map((c) => c?.value).filter(Boolean));
  return chosen;
};

// Query params carrying the HO business filter. Merged into every request's
// `params` so the selected business also travels as a query string (useful for
// GET endpoints that can't take a body). Empty when nothing is selected.
export const getHoFilterParams = () => {
  const businesses = getSelectedBusinesses();
  const categories = getSelectedCategories();
  const params = {};
  if (businesses.length) params.businesses = businesses;
  if (categories.length) params.categories = categories;
  return params;
};

const matchesTopLevel = (url) =>
  typeof url === "string" && TOP_LEVEL_ENDPOINTS.some((endpoint) => url.includes(endpoint));

// Inject the selected businesses into a request body for the endpoints that
// support HO org filtering. Only POST requests are touched — write/CRUD/auth
// endpoints (create/update/login/etc.) are left untouched. Returns the
// (possibly new) data object; callers' own values are preserved except for the
// injected `businesses` field.
export const applyHoFilters = (method, url, data) => {
  const businesses = getSelectedBusinesses();
  const categories = getSelectedCategories();
  if (!businesses.length && !categories.length) return data;
  if (typeof method !== "string" || method.toLowerCase() !== "post") return data;

  const body = data && typeof data === "object" && !Array.isArray(data) ? data : {};

  const extra = {};
  if (businesses.length) extra.businesses = businesses;
  if (categories.length) extra.categories = categories;

  if (isSearchEndpoint(url) || matchesTopLevel(url)) {
    return data?.searchFilter ? { ...body, searchFilter: { ...data?.searchFilter, ...extra } } : { ...body, ...extra };
  }
  return data;
};
